import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Send, RotateCw, Trash2 } from 'lucide-react';
import useAutoSave from '../hooks/useAutoSave';
import * as SignService from '../service/sign';
import { reportColumnCopy } from './columnCopy';
import { validateEnvelope, maxReachableStep, stepHint } from './editor/validation';
import { MAX_SIGNERS } from './editor/sign-overlays';
import Step1Documents from './editor/Step1Documents';
import Step2MapFields from './editor/Step2MapFields';
import StepNav from './editor/StepNav';
import FooterHint from './editor/FooterHint';
import SendChecklist from './editor/SendChecklist';

const STEPS = [
  { n: 1, label: 'Documents' },
  { n: 2, label: 'Map fields & signers' },
];
const LAST_STEP = STEPS.length;
const MAX_DOCUMENTS = 10;

/**
 * The envelope builder — tab 1's two-step wizard.
 *
 * It was three. The third held five cards; four became board settings, and the
 * fifth — the parties — moved into step 2's properties panel, where the signer
 * being configured is already selected. See SignSignerPanel.
 *
 * The two request-level controls that briefly lived in a panel of their own are
 * placed rather than collected: signing order sits with the signer roster, since
 * it is a statement about those people, and link expiry is board policy seeded
 * onto the envelope at create. The send checklist moved to the footer, beside
 * the button it explains — see SendChecklist.
 *
 * Opens directly rather than behind a launcher, and deliberately does NOT create
 * anything on mount: the draft row is created by the first document that lands
 * (`ensureEnvelope`). Opening the tab and walking away therefore leaves nothing
 * behind, which is what made "open the builder immediately" safe.
 *
 * Everything autosaves, so leaving mid-build loses nothing — the draft shows up
 * in the drafts list to be resumed.
 */
export default function EnvelopeBuilder({
  monday,
  getSessionToken,
  boardId,
  itemId,
  envelopeId: initialEnvelopeId,
  board,
  templates,
  onBoardChanged,
  /* Opens the Settings tab. Optional: the request panel only offers the link
     when a BOARD-scoped problem is blocking the send, and only when it has
     somewhere to send them. */
  onOpenSettings,
  onSent,
  onDiscarded,
  onManageTemplates,
  onEditTemplate,
  onCreateTemplate,
}) {
  const [envelopeId, setEnvelopeId] = useState(initialEnvelopeId ?? null);
  const [envelope, setEnvelope] = useState(null);
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [itemFiles, setItemFiles] = useState(null);
  const [loadError, setLoadError] = useState(false);
  // The canvas needs document URLs synchronously (react-pdf takes a string), so
  // the session token is resolved once and held rather than awaited per render.
  const [sessionToken, setSessionToken] = useState(null);
  /*
    The board's policy — wording, identity check, notifications, and where
    signed PDFs land. Read-only here: the builder cannot change any of it, and
    the send re-reads it server-side anyway. It is loaded for ONE reason, which
    is that a board with no file column cannot send, and the request panel has
    to be able to say so before the button is pressed rather than after.
  */
  const [boardSettings, setBoardSettings] = useState(null);
  /** Which signer's address is being read off the item, if any. */
  const [resolvingSigner, setResolvingSigner] = useState(null);
  const creatingRef = useRef(null);
  /** Per-document debounce timers for inline renames. */
  const renameTimers = useRef({});

  const metaRef = useRef(null);
  const fieldsRef = useRef(null);

  // ── autosave, split like the template editor: meta and fields save apart so
  // a field drag doesn't push a half-typed signer name, and vice versa.
  const { autoSave: autoSaveMeta, formSaving: metaSaving } = useAutoSave({
    saveFn: async (token, payload) => {
      if (!envelopeId) return;
      await SignService.updateEnvelope(token, envelopeId, payload);
    },
    getSessionToken,
    formRef: metaRef,
    delay: 700,
  });
  const { autoSave: autoSaveFields, formSaving: fieldsSaving } = useAutoSave({
    saveFn: async (token, payload) => {
      if (!envelopeId) return;
      await SignService.saveEnvelopeFields(token, envelopeId, payload);
    },
    getSessionToken,
    formRef: fieldsRef,
    delay: 700,
  });

  const load = useCallback(
    async (id) => {
      try {
        const token = await getSessionToken();
        const res = await SignService.getEnvelope(token, id);
        if (res?.ok) setEnvelope(res.envelope);
        else setLoadError(true);
      } catch (err) {
        console.error('Failed to load envelope', err);
        setLoadError(true);
      }
    },
    [getSessionToken],
  );

  // Load ONLY when resuming a draft created elsewhere. Deliberately keyed to
  // the prop, not to `envelopeId`: creating an envelope sets that state, and a
  // fetch fired from the change would resolve AFTER the upload that triggered
  // the create and overwrite its result — the document would vanish from the
  // list it had just been added to.
  useEffect(() => {
    if (initialEnvelopeId) load(initialEnvelopeId);
  }, [initialEnvelopeId, load]);

  useEffect(() => {
    let cancelled = false;
    getSessionToken().then((t) => {
      if (!cancelled) setSessionToken(t);
    });
    return () => {
      cancelled = true;
    };
  }, [getSessionToken]);

  // Board settings, for the send checklist. A failure is swallowed on purpose:
  // `validateEnvelope` skips the board checks when it has no settings, so a
  // fetch that fails leaves the client gate quieter than the server's rather
  // than blocking a send the server would have allowed.
  useEffect(() => {
    if (!boardId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const token = await getSessionToken();
        const res = await SignService.getBoardSettings(token, boardId);
        if (!cancelled && res?.ok) setBoardSettings(res.settings);
      } catch (err) {
        console.error('Failed to load board settings', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [boardId, getSessionToken]);

  /**
   * Resolve the item's draft on demand — called by the first document add.
   *
   * The ref guards against two rapid adds racing two calls; the server guards
   * the rest, returning the item's existing draft rather than a second one, and
   * refusing with `request_in_flight` if a request went out since this tab
   * loaded. That refusal is a real race, not a theoretical one: a colleague on
   * the same item can send while this builder sits open.
   */
  const ensureEnvelope = useCallback(async () => {
    if (envelopeId) return envelopeId;
    if (creatingRef.current) return creatingRef.current;

    creatingRef.current = (async () => {
      const token = await getSessionToken();
      const res = await SignService.createEnvelope(token, boardId, itemId);
      if (!res?.ok) {
        if (res?.error === 'request_in_flight') {
          monday.execute('notice', { message: res.message, type: 'error', timeout: 6000 });
          // Hand the shell back control so it can re-resolve and show the blocker.
          onDiscarded();
        }
        throw new Error(res?.error || 'create_failed');
      }
      setEnvelopeId(res.envelope.id);
      setEnvelope(res.envelope);
      return res.envelope.id;
    })();

    try {
      return await creatingRef.current;
    } finally {
      creatingRef.current = null;
    }
  }, [envelopeId, getSessionToken, boardId, itemId, monday, onDiscarded]);

  const patchMeta = useCallback(
    (patch) => {
      setEnvelope((prev) => {
        if (!prev) return prev;
        const next = { ...prev, ...patch };
        metaRef.current = {
          name: next.name,
          signers: next.signers,
          signingOrder: next.signingOrder,
          sending: next.sending,
          output: next.output,
          requireOtp: next.requireOtp,
          notifications: next.notifications,
        };
        return next;
      });
      autoSaveMeta();
    },
    [autoSaveMeta],
  );

  const patchFields = useCallback(
    ({ prefillFields, signerFields }) => {
      setEnvelope((prev) => (prev ? { ...prev, prefillFields, signerFields } : prev));
      fieldsRef.current = { prefillFields, signerFields };
      autoSaveFields();
    },
    [autoSaveFields],
  );

  // ── documents ──────────────────────────────────────────────────────────────

  const withBusy = async (fn) => {
    setBusy(true);
    try {
      return await fn();
    } finally {
      setBusy(false);
    }
  };

  const notifyFailure = (res, fallback) =>
    monday.execute('notice', {
      message: res?.message || fallback,
      type: 'error',
      timeout: 5000,
    });

  const handleUpload = (file) =>
    withBusy(async () => {
      const id = await ensureEnvelope();
      const token = await getSessionToken();
      const res = await SignService.uploadEnvelopeDocument(token, id, file);
      if (res?.ok) setEnvelope(res.envelope);
      else notifyFailure(res, 'Could not add that PDF.');
    });

  const handleAddFromTemplate = (templateId) =>
    withBusy(async () => {
      const id = await ensureEnvelope();
      const token = await getSessionToken();
      const res = await SignService.addEnvelopeDocumentFromTemplate(token, id, templateId);
      if (!res?.ok) {
        notifyFailure(res, 'Could not use that template.');
        return;
      }
      setEnvelope(res.envelope);
      // Surfaced rather than silent: a party that did not fit would otherwise
      // just be missing from step 3.
      if (res.overflow?.length) {
        monday.execute('notice', {
          message: `${res.overflow.join(', ')} could not be added — ${MAX_SIGNERS} signers is the maximum.`,
          type: 'error',
          timeout: 6000,
        });
      }
      if (res.fieldsDropped > 0) {
        monday.execute('notice', {
          message: `${res.fieldsDropped} field(s) from the template could not be copied.`,
          type: 'error',
          timeout: 6000,
        });
      }
    });

  const handleAddFromColumn = (columnId, columnTitle) =>
    withBusy(async () => {
      const id = await ensureEnvelope();
      const token = await getSessionToken();
      const res = await SignService.addEnvelopeDocumentFromColumn(token, id, columnId, columnTitle);
      if (!res?.ok) {
        notifyFailure(res, 'Could not add those files.');
        return;
      }
      setEnvelope(res.envelope);
      reportColumnCopy(monday, res, columnTitle);
    });

  const handleRemoveDocument = async (documentId) => {
    const doc = envelope?.documents?.find((d) => d.id === documentId);
    const fieldCount = [...(envelope?.prefillFields || []), ...(envelope?.signerFields || [])].filter(
      (f) => f.documentId === documentId,
    ).length;

    if (fieldCount > 0) {
      const confirmed = await monday
        .execute('confirm', {
          message: `Remove “${doc?.name || 'this document'}”? The ${fieldCount} field${
            fieldCount === 1 ? '' : 's'
          } placed on it will be removed too.`,
          confirmButton: 'Remove',
          cancelButton: 'Cancel',
        })
        .then((res) => res?.data?.confirm)
        .catch(() => window.confirm('Remove this document and its fields?'));
      if (!confirmed) return;
    }

    const token = await getSessionToken();
    const res = await SignService.removeEnvelopeDocument(token, envelopeId, documentId);
    if (res?.ok) setEnvelope(res.envelope);
    else notifyFailure(res, 'Could not remove that document.');
  };

  const handleReorder = async (order) => {
    // Optimistic: the drag already moved the row under the cursor.
    setEnvelope((prev) =>
      prev
        ? { ...prev, documents: order.map((id) => prev.documents.find((d) => d.id === id)).filter(Boolean) }
        : prev,
    );
    const token = await getSessionToken();
    const res = await SignService.reorderEnvelopeDocuments(token, envelopeId, order);
    if (res?.ok) setEnvelope(res.envelope);
  };

  /**
   * Rename a document. Optimistic locally, debounced to the server so typing
   * does not fire a request per keystroke. Deliberately NOT folded into the
   * meta autosave: that payload has no documents field, so the name would look
   * saved and silently vanish on the next load.
   */
  const handleRenameDocument = (documentId, name) => {
    setEnvelope((prev) =>
      prev
        ? { ...prev, documents: prev.documents.map((d) => (d.id === documentId ? { ...d, name } : d)) }
        : prev,
    );
    clearTimeout(renameTimers.current[documentId]);
    renameTimers.current[documentId] = setTimeout(async () => {
      const token = await getSessionToken();
      await SignService.renameEnvelopeDocument(token, envelopeId, documentId, name);
    }, 700);
  };

  /**
   * The item's File columns, each with the PDFs it currently holds.
   *
   * Fetched per column with aliases in ONE round trip: `Asset` carries no
   * column attribution, so a flat `assets` query cannot say which column a
   * file came from — and the column is now the unit the user picks.
   *
   * Counts only; the server re-reads the column when you actually add, because
   * "everything in the column" has to mean everything in it then, not when this
   * tab was opened.
   */
  const loadItemFiles = useCallback(async () => {
    if (itemFiles !== null || !itemId) return;
    // Bail WITHOUT recording a result: the board loads asynchronously, and
    // caching "no columns" here would be permanent — the `itemFiles !== null`
    // guard above stops the retry once the board arrives.
    if (!board) return;
    const columns = board.fileColumns || [];
    if (!columns.length) {
      setItemFiles([]);
      return;
    }
    try {
      const selections = columns
        .map((col, i) => `c${i}: assets (column_ids: ["${col.id}"]) { id name file_extension }`)
        .join(' ');
      const res = await monday.api(
        `query ($ids: [ID!]) { items (ids: $ids) { ${selections} } }`,
        { variables: { ids: [String(itemId)] } },
      );
      const item = res?.data?.items?.[0] || {};
      setItemFiles(
        columns.map((col, i) => ({
          id: col.id,
          title: col.title,
          pdfCount: (item[`c${i}`] || []).filter(
            (a) => String(a.file_extension || '').toLowerCase().replace('.', '') === 'pdf',
          ).length,
        })),
      );
    } catch (err) {
      console.error('Failed to list item file columns', err);
      setItemFiles([]);
    }
  }, [itemFiles, itemId, monday, board]);

  useEffect(() => {
    loadItemFiles();
  }, [loadItemFiles]);

  // ── send ───────────────────────────────────────────────────────────────────

  const handleSend = async () => {
    if (sending || !envelopeId) return;
    setSending(true);
    try {
      const token = await getSessionToken();
      const res = await SignService.sendEnvelope(token, envelopeId);
      if (res?.ok) {
        monday.execute('notice', {
          message: 'Signature request sent — track it in the next tab.',
          type: 'success',
          timeout: 5000,
        });
        (res.warnings || []).forEach((w) =>
          monday.execute('notice', { message: w, type: 'error', timeout: 6000 }),
        );
        onSent();
      } else if (res?.errors?.length) {
        // The server validated against a fresh board read — its verdict wins,
        // so jump to the step it names.
        const first = res.errors[0];
        monday.execute('notice', {
          message: `Can't send yet: ${first.message}`,
          type: 'error',
          timeout: 6000,
        });
        setStep(first.step || 3);
      } else {
        notifyFailure(res, 'Could not send the request.');
      }
    } catch (err) {
      console.error('Send failed', err);
      monday.execute('notice', { message: 'Could not send the request.', type: 'error' });
    } finally {
      setSending(false);
    }
  };

  const handleDiscard = async () => {
    if (!envelopeId) {
      onDiscarded();
      return;
    }
    const confirmed = await monday
      .execute('confirm', {
        message: 'Discard this draft? Its documents and field placements will be deleted.',
        confirmButton: 'Discard',
        cancelButton: 'Keep it',
      })
      .then((res) => res?.data?.confirm)
      .catch(() => window.confirm('Discard this draft?'));
    if (!confirmed) return;

    const token = await getSessionToken();
    await SignService.deleteEnvelope(token, envelopeId);
    onDiscarded();
  };

  // ── render ─────────────────────────────────────────────────────────────────

  if (loadError) {
    return (
      <div className='flex flex-col items-center justify-center h-full gap-3 bg-gray-50'>
        <div className='text-sm text-gray-600'>This request could not be loaded.</div>
        <button
          onClick={onDiscarded}
          className='px-3 py-1.5 rounded-md text-sm font-medium bg-gray-900 text-white'
        >
          Start over
        </button>
      </div>
    );
  }

  /** Patch ONE signer. Used by the properties panel for name, column and address. */
  const patchSigner = (index, patch) =>
    patchMeta({
      signers: (envelope?.signers || []).map((sg, i) => (i === index ? { ...sg, ...patch } : sg)),
    });

  /**
   * Pull an address off the item for one signer — one pick, then editable.
   *
   * The column is stored immediately and the address follows, so a column that
   * resolves to nothing still records the intent rather than silently doing
   * nothing.
   */
  const resolveSignerEmail = async (index, columnId) => {
    patchSigner(index, { emailColumnId: columnId || null });
    if (!columnId || !itemId) return;
    setResolvingSigner(index);
    try {
      const token = await getSessionToken();
      const res = await SignService.resolveItemEmails(token, itemId, [columnId]);
      const email = res?.emails?.[columnId] || '';
      // An empty column is reported by the panel, not by a toast: the column is
      // still recorded above, so "chosen but nothing in it" is a state the panel
      // can read off the signer and keep saying for as long as it is true. A
      // notice says it once, four seconds after you looked away.
      if (email) patchSigner(index, { emailColumnId: columnId, email });
    } catch (err) {
      console.error('Failed to resolve item email', err);
    } finally {
      setResolvingSigner(null);
    }
  };

  // Before the first document there is no envelope yet — step 1 still renders,
  // which is the whole point of creating on demand.
  const subject = envelope || {
    documents: [],
    signers: [],
    prefillFields: [],
    signerFields: [],
    sending: {},
    output: {},
    signingOrder: 'sequential',
    boardId,
  };

  const reachable = maxReachableStep(subject);
  const { canSend, errors, warnings } = validateEnvelope(subject, board, boardSettings);
  const hint = stepHint(subject, board, step, 'envelope', boardSettings);
  const documentUrl = (documentId) =>
    sessionToken ? SignService.getEnvelopeDocumentUrl(sessionToken, documentId) : null;

  return (
    <div className='flex flex-col h-full flex-1 min-h-0 overflow-hidden bg-gray-50'>
      {/* ── Wizard chrome ── */}
      <header className='flex-shrink-0 flex items-center gap-3 px-4 py-2 bg-white border-b border-gray-200'>
        <div className='min-w-0'>
          <div className='text-sm font-semibold text-gray-900 leading-tight truncate'>
            {subject.name || 'New signing request'}
          </div>
          <div className='text-xs text-gray-500 leading-tight'>
            {subject.documents.length
              ? `${subject.documents.length} document${subject.documents.length === 1 ? '' : 's'}`
              : 'Add a document to begin'}
          </div>
        </div>

        <div className='flex-1'>
          <StepNav steps={STEPS} step={step} reachable={reachable} onGo={setStep} />
        </div>

        <div className='flex items-center gap-2 flex-shrink-0'>
          <span className='text-xs text-gray-400 w-14 text-right'>
            {metaSaving || fieldsSaving ? (
              <span className='inline-flex items-center gap-1'>
                <RotateCw size={11} className='animate-spin' />
                Saving
              </span>
            ) : envelopeId ? (
              'Saved'
            ) : (
              ''
            )}
          </span>
          {envelopeId && (
            <button
              onClick={handleDiscard}
              title='Discard this draft'
              className='p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50'
            >
              <Trash2 size={15} />
            </button>
          )}
        </div>
      </header>

      {/* ── Step body ── */}
      <main className='flex-1 overflow-hidden flex'>
        {step === 1 && (
          <Step1Documents
            documents={subject.documents}
            templates={templates}
            itemFiles={itemFiles}
            busy={busy}
            maxDocuments={MAX_DOCUMENTS}
            onUpload={handleUpload}
            onAddFromTemplate={handleAddFromTemplate}
            onAddFromColumn={handleAddFromColumn}
            onRemove={handleRemoveDocument}
            onReorder={handleReorder}
            onRename={handleRenameDocument}
            onManageTemplates={onManageTemplates}
            onEditTemplate={onEditTemplate}
            onCreateTemplate={onCreateTemplate}
          />
        )}
        {step === 2 && (
          <Step2MapFields
            subject={subject}
            board={board}
            boardSettings={boardSettings}
            documentUrl={documentUrl}
            onFieldsChange={patchFields}
            onPatchSigner={patchSigner}
            onResolveSignerEmail={resolveSignerEmail}
            resolvingSigner={resolvingSigner}
            /* The create-column modal lives on step 3's card today. Until it is
               rebuilt here, picking "+ Create new email column…" tells the user
               where to make one rather than opening nothing. */
            onCreateEmailColumn={() =>
              monday.execute('notice', {
                message: 'Add an email column to the board, then pick it here.',
                type: 'info',
                timeout: 5000,
              })
            }
            onPatchRequest={patchMeta}
            onAddSigner={() => {
              if (subject.signers.length >= MAX_SIGNERS) return;
              patchMeta({
                signers: [
                  ...subject.signers,
                  { roleLabel: `Signer ${subject.signers.length + 1}`, email: '', emailColumnId: null },
                ],
              });
            }}
            onRenameSigner={(index, roleLabel) =>
              patchMeta({
                signers: subject.signers.map((sg, i) => (i === index ? { ...sg, roleLabel } : sg)),
              })
            }
            onRemoveSigner={async (index) => {
              if (subject.signers.length <= 1) return;
              const fieldCount = (subject.signerFields || []).filter((f) => f.signerIndex === index).length;
              if (fieldCount > 0) {
                const confirmed = await monday
                  .execute('confirm', {
                    message: `Remove ${subject.signers[index]?.roleLabel || 'this signer'}? Their ${fieldCount} placed field${
                      fieldCount === 1 ? '' : 's'
                    } will be removed too.`,
                    confirmButton: 'Remove signer',
                    cancelButton: 'Cancel',
                  })
                  .then((res) => res?.data?.confirm)
                  .catch(() => window.confirm('Remove this signer and their fields?'));
                if (!confirmed) return;
              }
              // Later signers shift down, so their fields must follow.
              patchFields({
                prefillFields: subject.prefillFields || [],
                signerFields: (subject.signerFields || [])
                  .filter((f) => f.signerIndex !== index)
                  .map((f) => (f.signerIndex > index ? { ...f, signerIndex: f.signerIndex - 1 } : f)),
              });
              patchMeta({ signers: subject.signers.filter((_, i) => i !== index) });
            }}
          />
        )}
      </main>

      {/* ── Footer ── */}
      <footer className='flex-shrink-0 flex items-center gap-3 px-4 py-2.5 bg-white border-t border-gray-200'>
        <button
          onClick={() => setStep((s) => Math.max(1, s - 1))}
          className={`px-3 py-1.5 rounded-md text-sm font-medium border border-gray-200 text-gray-700 hover:border-gray-300 ${
            step === 1 ? 'invisible' : ''
          }`}
        >
          Back
        </button>

        {/* Spacer, then the explanation beside the button it explains. */}
        <span className='flex-1' />
        {step === LAST_STEP ? (
          /* On the last step the checklist says everything the hint would, and
             all of it at once rather than the first item only. */
          <SendChecklist
            errors={errors}
            warnings={warnings}
            documentCount={subject.documents.length}
            onOpenSettings={onOpenSettings}
          />
        ) : (
          <FooterHint hint={hint} />
        )}

        {step === LAST_STEP ? (
          <button
            onClick={handleSend}
            disabled={sending || !canSend}
            title={canSend ? 'Send for signature' : 'Finish the highlighted steps first'}
            className='flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed'
          >
            {sending ? <RotateCw size={13} className='animate-spin' /> : <Send size={13} />}
            Send for signature
          </button>
        ) : (
          <button
            onClick={() => setStep((s) => Math.min(reachable, s + 1))}
            disabled={step >= reachable}
            className='px-4 py-1.5 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed'
          >
            Next
          </button>
        )}
      </footer>
    </div>
  );
}
